import { useEffect } from "react";
import { useAuth } from "@/lib/auth";

/**
 * ZohoChatWidget
 *
 * Lazily injects the Zoho SalesIQ chat widget script into the document ONLY
 * when an authenticated user is in session. On logout (or if /auth/me 401s
 * and clears the user), we tear the widget back out of the DOM so visitors
 * on marketing pages never see it.
 *
 * The widget keys off the two globals Zoho's loader expects:
 *   • `window.$zoho.salesiq`
 *   • `<script id="zsiqscript" src="…widget?wc=…">`
 */
const WIDGET_ID = "zsiqscript";
const WIDGET_SRC = "https://salesiq.zoho.com/widget?wc=siqe2b0387167eb5198dbdd9baa4b1e91eb6e47112f819df5ac7be7c8977c8455fc";

export default function ZohoChatWidget() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading) return;

    if (!user) {
      // No auth → make sure the widget is NOT loaded (handles logout).
      removeWidget();
      return;
    }

    // Already injected? Nothing to do.
    if (document.getElementById(WIDGET_ID)) return;

    // Minimal boot shim Zoho's loader expects before the main script runs.
    if (!window.$zoho) window.$zoho = {};
    if (!window.$zoho.salesiq) window.$zoho.salesiq = { ready() {} };

    const s = document.createElement("script");
    s.id = WIDGET_ID;
    s.src = WIDGET_SRC;
    s.defer = true;
    document.body.appendChild(s);
    // No cleanup on unmount — the widget should persist across route changes
    // for the duration of the authenticated session. Removal happens only
    // on logout (handled by the `!user` branch above).
  }, [user, loading]);

  return null;
}

/** Remove script + any DOM nodes the SalesIQ bundle injects on load. */
function removeWidget() {
  const script = document.getElementById(WIDGET_ID);
  if (script) script.remove();
  // SalesIQ renders its floating button + chat frame into these containers.
  // Clearing them on logout prevents the chat bubble from lingering.
  ["zsiqwidget", "siq_bR", "siqembed", "zsiq_float"].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.remove();
  });
  document.querySelectorAll('[id^="zsiq_"], [class^="zsiq_"]').forEach((el) => el.remove());
  // Tear down the globals so a subsequent login re-boots the widget fresh.
  if (window.$zoho) delete window.$zoho;
}
